// ABOUTME: Form for Dispatch Center Operational Hours configuration.
// ABOUTME: Includes timezone selector; more sections will be added incrementally.
import React, {useCallback, useEffect, useId, useMemo} from 'react'
import {Controller, useForm, useWatch} from 'react-hook-form'
import {Icon} from '@hm/fundamental'
import {CountryConfigsTooltip} from './CountryConfigsTooltip'
import { mdiInformationOutline} from '@mdi/js'

import {
  OperationalHoursFormProps,
  OperationalHoursFormValues,
  OperationalHoursConfig,
  OperatingHoursEntry,
} from '../types/operationalHours'
import {DayIdentifier, DAYS} from '../types/deliveryWindow'
import {DAY_FULL_LABELS} from '../constants'
import {OperatingHoursColumn, OperatingHoursRow} from '../types/operatingHoursTable'
import {useTranslation} from '../../../../hooks/useTranslation'
import {validateSingleHours} from '../utils'
import {dayCodesToIdentifiers} from '../utils/entityCatalogOptions'
import {useTimeZoneValidation} from '../hooks/useTimeZoneValidation'
import {DateChipPicker} from './DateChipPicker'
import {DayChipToggle} from './DayChipToggle'
import {OperatingHoursTable} from './OperatingHoursTable'
import {SectionTitle} from './SectionTitle'
import { CountryConfigsTimezoneSelect } from '../CountryConfigsTimezoneSelect'


const toFormValues = (cfg: OperationalHoursConfig): OperationalHoursFormValues => ({
  timeZone: cfg.timeZone,
  businessDays: cfg.businessDays,
  standardDays: cfg.standardDays,
  standardHours: cfg.standardHours,
  nonStandardHours: cfg.nonStandardHours,
  nonWorkingDays: cfg.nonWorkingDays,
})

export const OperationalHoursForm: React.FC<OperationalHoursFormProps> = ({
  config,
  isReadOnly,
  onDirtyChange,
  onValidChange,
  onValuesChange,
  dayCodes,
}) => {
  const {t} = useTranslation()
  const timeZoneLabelId = useId()
  const timeZoneErrorId = useId()
  const allowedDays = useMemo(() => dayCodesToIdentifiers(dayCodes) ?? DAYS, [dayCodes])
  const formDefaultValues = useMemo(() => toFormValues(config), [config])
  const {control, setValue, getValues, reset, formState: {isDirty, dirtyFields}} = useForm<OperationalHoursFormValues>({
    defaultValues: formDefaultValues,
  })

  useEffect(() => {
    reset(formDefaultValues)
  }, [formDefaultValues, reset])

  const timeZone = useWatch({control, name: 'timeZone'})
  const businessDays = useWatch({control, name: 'businessDays'})
  const standardDays = useWatch({control, name: 'standardDays'})
  const standardHours = useWatch({control, name: 'standardHours'})
  const nonStandardHours = useWatch({control, name: 'nonStandardHours'})
  const nonWorkingDays = useWatch({control, name: 'nonWorkingDays'})

  // Render the union of backend-allowed days and currently-selected business days (canonical
  // order) so a day selected in the saved config but missing from the backend dayCodes still
  // shows a chip and stays removable. standardDays is always a subset of businessDays.
  const availableDays = useMemo(
    () => DAYS.filter((d) => allowedDays.includes(d) || (businessDays ?? []).includes(d)),
    [allowedDays, businessDays]
  )

  const values = useMemo<OperationalHoursFormValues>(() => ({
    timeZone: timeZone ?? '',
    businessDays: businessDays ?? [],
    standardDays: standardDays ?? [],
    standardHours: standardHours ?? {opens: '', prePlanning: '', closes: ''},
    nonStandardHours: nonStandardHours ?? {},
    nonWorkingDays: nonWorkingDays ?? [],
  }), [timeZone, businessDays, standardDays, standardHours, nonStandardHours, nonWorkingDays])
  const handleBusinessDaysChange = useCallback((next: DayIdentifier[]) => {
    const prev = getValues()
    setValue('businessDays', next, {shouldDirty: true})

    // Remove deselected business days from standard days
    const validStandardDays = prev.standardDays.filter((d) => next.includes(d))
    const sortedStandardDays = DAYS.filter((d) => validStandardDays.includes(d))
    setValue('standardDays', sortedStandardDays, {shouldDirty: true})

    // Clean up non-standard hours for days no longer in business days
    const validNonStandard = Object.fromEntries(
      Object.entries(prev.nonStandardHours).filter(([d]) =>
        next.includes(d as DayIdentifier) && !sortedStandardDays.includes(d as DayIdentifier)
      )
    )
    setValue('nonStandardHours', validNonStandard, {shouldDirty: true})
  }, [getValues, setValue])

  const handleStandardDaysChange = useCallback((next: DayIdentifier[]) => {
    const prev = getValues()
    setValue('standardDays', next, {shouldDirty: true})

    const validNonStandard = Object.fromEntries(
      Object.entries(prev.nonStandardHours).filter(([d]) => !next.includes(d as DayIdentifier))
    )
    setValue('nonStandardHours', validNonStandard, {shouldDirty: true})
  }, [getValues, setValue])

  const handleStandardHoursChange = useCallback((field: keyof OperatingHoursEntry, value: string) => {
    setValue(`standardHours.${field}`, value, {shouldDirty: true})
  }, [setValue])

  const columns: OperatingHoursColumn[] = useMemo(
    () => [
      {key: 'opens', label: t('countryConfigurations.operationalHours.standardHours.opens'), mode: 'single' as const},
      {key: 'prePlanning', label: t('countryConfigurations.operationalHours.standardHours.prePlanning'), mode: 'single' as const},
      {key: 'closes', label: t('countryConfigurations.operationalHours.standardHours.closes'), mode: 'single' as const},
    ],
    [t]
  )

  const standardDaysDisabled = useMemo(
    () => DAYS.filter((d) => !businessDays.includes(d)),
    [businessDays]
  )

  const nonStandardDays = useMemo(
    () => DAYS.filter((d) => businessDays.includes(d) && !standardDays.includes(d)),
    [businessDays, standardDays]
  )

  const nonStandardRows: OperatingHoursRow[] = useMemo(
    () => nonStandardDays.map((day) => {
      const hours = nonStandardHours?.[day] ?? {opens: '', prePlanning: '', closes: ''}
      return {
        key: day,
        label: t(DAY_FULL_LABELS[day]),
        values: {
          opens: hours.opens,
          prePlanning: hours.prePlanning,
          closes: hours.closes,
        },
      }
    }),
    [nonStandardDays, nonStandardHours, t]
  )

  const handleNonStandardHoursChange = useCallback((rowKey: string, columnKey: string, value: string) => {
    const prev = getValues()
    setValue('nonStandardHours', {
      ...prev.nonStandardHours,
      [rowKey]: {
        ...(prev.nonStandardHours?.[rowKey] ?? {opens: '', prePlanning: '', closes: ''}),
        [columnKey]: value,
      },
    }, {shouldDirty: true})
  }, [getValues, setValue])

  // When a new non-standard day is added, seed its hours from the current standard hours.
  useEffect(() => {
    const {nonStandardHours: currentNonStd, standardHours: currentStd} = getValues()
    const newDays = nonStandardDays.filter((day) => !currentNonStd?.[day])
    if (newDays.length === 0) return
    const updated = {...currentNonStd}
    for (const day of newDays) {
      updated[day] = {
        opens: currentStd?.opens ?? '',
        prePlanning: currentStd?.prePlanning ?? '',
        closes: currentStd?.closes ?? '',
      }
    }
    setValue('nonStandardHours', updated)
  }, [nonStandardDays, getValues, setValue])

  const standardTableErrors = useMemo(() => {
    const raw = validateSingleHours(
      [{key: 'standard', values: {opens: standardHours?.opens ?? '', prePlanning: standardHours?.prePlanning ?? '', closes: standardHours?.closes ?? ''}}],
      t,
    )
    return Object.fromEntries(Object.entries(raw).map(([k, v]) => [k.replace(/^0\./, 'standard.'), v]))
  }, [standardHours, t])

  const nonStandardTableErrors = useMemo(() => {
    const raw = validateSingleHours(
      nonStandardRows.map((row) => ({key: row.key, values: row.values as {opens: string; prePlanning: string; closes: string}})),
      t,
    )
    const mapped: Record<string, string> = {}
    for (const [k, v] of Object.entries(raw)) {
      const dot = k.indexOf('.')
      const idx = parseInt(k.substring(0, dot), 10)
      const rest = k.substring(dot + 1)
      const day = nonStandardRows[idx]?.key
      if (day) mapped[`${day}.${rest}`] = v
    }
    return mapped
  }, [nonStandardRows, t])

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  const timeZoneInvalidError = useTimeZoneValidation(
    timeZone ?? '',
    Boolean(dirtyFields.timeZone),
    t('countryConfigurations.operationalHours.timeZone.invalid'),
  )
  // Saving always overrides the time zone, and the backend rejects an empty one.
  const timeZoneError = (timeZone ?? '').trim()
    ? timeZoneInvalidError
    : t('countryConfigurations.operationalHours.timeZone.required')

  const businessDaysError = businessDays.length === 0
    ? t('countryConfigurations.operationalHours.validation.businessDaysRequired')
    : undefined
  const standardDaysError = businessDays.length > 0 && standardDays.length === 0
    ? t('countryConfigurations.operationalHours.validation.standardDaysRequired')
    : undefined

  useEffect(() => {
    const hasErrors =
      Object.keys(standardTableErrors).length > 0 ||
      Object.keys(nonStandardTableErrors).length > 0 ||
      (config.nonWorkingDays.length > 0 && (nonWorkingDays?.length ?? 0) === 0) ||
      Boolean(timeZoneError) ||
      Boolean(businessDaysError) ||
      Boolean(standardDaysError)
    onValidChange(!hasErrors)
  }, [onValidChange, standardTableErrors, nonStandardTableErrors, nonWorkingDays, config.nonWorkingDays.length, timeZoneError, businessDaysError, standardDaysError])

  useEffect(() => {
    if (isDirty) {
      onValuesChange?.(values)
    }
  }, [values, isDirty, onValuesChange])

  return (
    <div className="flex flex-col gap-6 p-6 w-full" data-cy="operational-hours-form">
      <div className="flex gap-6 items-center">
        <div className="flex items-center gap-1 min-w-[200px] max-w-[300px]">
          <span id={timeZoneLabelId} className="text-base font-medium text-foreground">{t('countryConfigurations.operationalHours.timeZone.label')}</span>
          <CountryConfigsTooltip content={t('countryConfigurations.operationalHours.timeZone.tooltip')} delayMs={300}>
            <span className="self-center shrink-0 flex cursor-default" aria-label="More information">
              <Icon path={mdiInformationOutline} size="regular" className="text-foreground" />
            </span>
          </CountryConfigsTooltip>
        </div>
        <div className="relative w-[240px]">
          <Controller
            name="timeZone"
            control={control}
            render={({ field }) => (
              <CountryConfigsTimezoneSelect
                value={field.value}
                onChange={field.onChange}
                disabled={isReadOnly}
                extraOptions={[config.timeZone]}
                placeholderKey="countryConfigurations.operationalHours.timeZone.placeholder"
                searchPlaceholderKey="countryConfigurations.operationalHours.timeZone.searchPlaceholder"
                noResultsKey="countryConfigurations.operationalHours.timeZone.noResults"
                aria-labelledby={timeZoneLabelId}
                aria-describedby={timeZoneError ? timeZoneErrorId : undefined}
              />
            )}
          />
          {timeZoneError && <p id={timeZoneErrorId} role="alert" className="text-xs leading-4 text-negative px-3">{timeZoneError}</p>}
        </div>
      </div>
      <hr className="border-border" />

      {/* Business days section */}
      <div className="flex flex-col gap-1">
        <SectionTitle
          titleKey="countryConfigurations.operationalHours.businessDays.title"
          tooltipKey="countryConfigurations.operationalHours.businessDays.tooltip"
        />
        <DayChipToggle
          value={businessDays}
          onChange={handleBusinessDaysChange}
          days={availableDays}
          disabled={isReadOnly}
          data-cy="operational-hours-business-days"
        />
        {businessDaysError && <p role="alert" className="text-xs leading-4 text-negative">{businessDaysError}</p>}
      </div>

      {businessDays.length > 0 && (
        <>
          <hr className="border-border" />

          {/* Standard operating hours section */}
          <div className="flex flex-col gap-1">
            <SectionTitle
              titleKey="countryConfigurations.operationalHours.standardHours.title"
              tooltipKey="countryConfigurations.operationalHours.standardHours.tooltip"
              subtitle="countryConfigurations.operationalHours.standardHours.subtitle"
            />

            <div className="flex flex-col gap-3.5 mt-4">
              <span className="text-sm font-medium text-foreground_faded">
                {t('countryConfigurations.operationalHours.standardHours.selectStandardDays')}
              </span>
              <DayChipToggle
                value={standardDays}
                onChange={handleStandardDaysChange}
                days={availableDays}
                disabled={isReadOnly}
                disabledDays={standardDaysDisabled}
                data-cy="operational-hours-standard-days"
              />
              {standardDaysError && <p role="alert" className="text-xs leading-4 text-negative">{standardDaysError}</p>}
            </div>

            {standardDays.length > 0 && (
              <div className="mt-4">
                <OperatingHoursTable
                  columns={columns}
                  errors={standardTableErrors}
                  rows={[{
                    key: 'standard',
                    label: t('countryConfigurations.operationalHours.standardHours.rowLabel'),
                    values: {
                      opens: standardHours?.opens ?? '',
                      prePlanning: standardHours?.prePlanning ?? '',
                      closes: standardHours?.closes ?? '',
                    },
                  }]}
                  onTimeChange={(_rowKey, columnKey, value) => handleStandardHoursChange(columnKey as keyof OperatingHoursEntry, value)}
                  disabled={isReadOnly}
                  data-cy="operational-hours-standard-table"
                />
              </div>
            )}
          </div>

          <hr className="border-border" />

          {/* Non-standard operating hours section */}
          <div className="flex flex-col gap-1">
            <SectionTitle
              titleKey="countryConfigurations.operationalHours.nonStandardHours.title"
              tooltipKey="countryConfigurations.operationalHours.nonStandardHours.tooltip"
            />

            <span className="text-sm text-foreground_faded">
              {t('countryConfigurations.operationalHours.nonStandardHours.emptyState')}
            </span>
            {nonStandardRows.length > 0 && (
              <div className="mt-4">
                <OperatingHoursTable
                  columns={columns}
                  errors={nonStandardTableErrors}
                  rows={nonStandardRows}
                  onTimeChange={handleNonStandardHoursChange}
                  disabled={isReadOnly}
                  data-cy="operational-hours-non-standard-table"
                />
              </div>
            )}
          </div>

          <hr className="border-border" />

          {/* Non-working days section */}
          <div className="flex flex-col gap-1">
            <SectionTitle
              titleKey="countryConfigurations.operationalHours.nonWorkingDays.title"
              tooltipKey="countryConfigurations.operationalHours.nonWorkingDays.tooltip"
            />
            <p className="text-sm text-foreground_faded mb-4">
              {t('countryConfigurations.operationalHours.nonWorkingDays.subtitle')}
            </p>
            <Controller
              name="nonWorkingDays"
              control={control}
              render={({field}) => (
                <DateChipPicker
                  value={field.value ?? []}
                  initialValue={config.nonWorkingDays}
                  onChange={field.onChange}
                  disabled={isReadOnly}
                  error={config.nonWorkingDays.length > 0 && (field.value?.length ?? 0) === 0 ? t('countryConfigurations.operationalHours.validation.required') : undefined}
                  data-cy="operational-hours-non-working-days"
                />
              )}
            />
          </div>
        </>
      )}
    </div>
  )
}
